'use client';

import { useState, useEffect, type FormEvent } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [contactStatus, setContactStatus] = useState<{
    type: 'idle' | 'success' | 'error';
    message: string;
  }>({ type: 'idle', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isContactModalOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        setIsContactModalOpen(false);
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isContactModalOpen, isSubmitting]);

  const faqs: FAQ[] = [
    {
      question: 'How long do your products last without preservatives?',
      answer: 'Our pickles and murrabas last 12-18 months when stored properly in a cool, dry place. The natural fermentation process and traditional preservation methods using oil and spices act as natural preservatives. Our deshi ghee lasts up to 24 months.',
    },
    {
      question: 'Do you ship across India?',
      answer: 'Yes, we ship to all major cities and towns across India. Orders are typically delivered within 3-5 business days. We use specialized packaging to ensure products reach you fresh and intact. Free shipping is available on orders above ₹500.',
    },
    {
      question: 'Are your products really made without any preservatives?',
      answer: 'Absolutely! We use only traditional methods - natural fermentation, pure oils, and authentic spices. No sodium benzoate, potassium sorbate, or any artificial preservatives. All our products are FSSAI certified and regularly tested for quality.',
    },
    {
      question: 'Can I return products if I\'m not satisfied?',
      answer: 'Yes, we offer a 100% money-back taste guarantee. If you\'re not completely satisfied with the authentic taste and quality, contact us within 7 days of delivery for a full refund. Your satisfaction is our priority.',
    },
    {
      question: 'What makes your ghee different from store-bought brands?',
      answer: 'Our deshi ghee is made from A2 cow milk using the traditional bilona method - hand-churned and slow-cooked. Unlike commercial brands that use refined methods, we maintain the traditional process that preserves nutrients and authentic flavor.',
    },
    {
      question: 'Do you offer gift packaging for festivals?',
      answer: 'Yes! We offer premium gift packaging perfect for festivals like Diwali, Raksha Bandhan, and other occasions. You can select gift packaging during checkout. We also include personalized greeting cards on request.',
    },
    {
      question: 'Are your products suitable for people with dietary restrictions?',
      answer: 'Our products are 100% vegetarian and made with natural ingredients. However, some pickles contain mustard oil and spices. If you have specific allergies, please check the ingredient list on each product page or contact us for detailed information.',
    },
    {
      question: 'How do I know the products are fresh?',
      answer: 'Every product is made in small batches and includes a manufacturing date on the label. We never stock old inventory - what you receive is freshly made within 2-3 weeks of your order. You can also track the batch number for complete transparency.',
    },
  ];

  const handleToggle = (index: number) => {
    if (!isHydrated) return;
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setContactStatus({ type: 'idle', message: '' });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      const result = (await response.json()) as { message?: string };

      if (!response.ok) {
        throw new Error(result.message || 'Unable to send your message.');
      }

      setContactStatus({
        type: 'success',
        message: result.message || 'Your message has been sent.',
      });
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      setContactStatus({
        type: 'error',
        message:
          error instanceof Error
            ? error.message
            : 'Unable to send your message. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="faq" className="bg-muted py-16 lg:py-24">
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="mb-12 text-center">
          <h2 className="font-heading mb-4 text-3xl font-bold text-foreground lg:text-4xl">
            Frequently Asked <span className="text-primary">Questions</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Everything you need to know about our products and services
          </p>
        </div>

        <div className="mx-auto max-w-3xl space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="overflow-hidden rounded-xl bg-background shadow-cta"
            >
              <button
                onClick={() => handleToggle(index)}
                className="flex w-full items-center justify-between gap-4 p-6 text-left transition-colors duration-250 ease-out hover:bg-muted"
              >
                <span className="font-heading text-lg font-bold text-foreground">
                  {faq.question}
                </span>
                <Icon
                  name={
                    (isHydrated ? openIndex : null) === index
                      ? 'ChevronUpIcon' :'ChevronDownIcon'
                  }
                  size={24}
                  className="flex-shrink-0 text-primary transition-transform duration-250 ease-out"
                />
              </button>
              {(isHydrated ? openIndex : null) === index && (
                <div className="border-t border-border px-6 pb-6 pt-4">
                  <p className="text-muted-foreground">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl bg-primary/5 p-8 text-center">
          <Icon
            name="QuestionMarkCircleIcon"
            size={48}
            className="mx-auto mb-4 text-primary"
          />
          <h3 className="font-heading mb-2 text-2xl font-bold text-foreground">
            Still Have Questions?
          </h3>
          <p className="mb-6 text-muted-foreground">
            Our customer support team is here to help you
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="https://wa.me/919335295050"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg border-2 border-primary bg-background px-6 py-3 font-cta text-base font-bold text-primary transition-colors duration-250 ease-out hover:bg-primary/10"
            >
              <Icon name="PhoneIcon" size={20} />
              Call Us
            </a>
            <a
              href="#contact"
              onClick={(event) => {
                event.preventDefault();
                setContactStatus({ type: 'idle', message: '' });
                setIsContactModalOpen(true);
              }}
              className="flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-cta text-base font-bold text-primary-foreground shadow-cta transition-all duration-250 ease-out hover:shadow-lg"
            >
              <Icon name="EnvelopeIcon" size={20} />
              Email Us
            </a>
          </div>
        </div>
      </div>

      {isContactModalOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-foreground/60 px-4 py-6"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isSubmitting) {
              setIsContactModalOpen(false);
            }
          }}
        >
          <div
            className="max-h-full w-full max-w-lg overflow-y-auto rounded-2xl bg-background p-6 shadow-2xl sm:p-8"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-modal-title"
            aria-describedby="contact-modal-description"
          >
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h2
                  id="contact-modal-title"
                  className="font-heading text-2xl font-bold text-foreground"
                >
                  Contact Us
                </h2>
                <p
                  id="contact-modal-description"
                  className="mt-1 text-sm text-muted-foreground"
                >
                  Send us a message and our support team will get back to you.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsContactModalOpen(false)}
                disabled={isSubmitting}
                className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close contact form"
              >
                <Icon name="XMarkIcon" size={22} />
              </button>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-4">
              <label className="block text-left">
                <span className="mb-1 block text-sm font-bold text-foreground">
                  Name
                </span>
                <input
                  required
                  maxLength={100}
                  value={contactForm.name}
                  onChange={(event) =>
                    setContactForm({ ...contactForm, name: event.target.value })
                  }
                  className="w-full rounded-lg border border-border bg-background px-4 py-3 text-foreground outline-none transition-shadow focus:ring-2 focus:ring-primary"
                />
              </label>
              <label className="block text-left">
                <span className="mb-1 block text-sm font-bold text-foreground">
                  Email
                </span>
                <input
                  required
                  type="email"
                  maxLength={254}
                  value={contactForm.email}
                  onChange={(event) =>
                    setContactForm({ ...contactForm, email: event.target.value })
                  }
                  className="w-full rounded-lg border border-border bg-background px-4 py-3 text-foreground outline-none transition-shadow focus:ring-2 focus:ring-primary"
                />
              </label>
              <label className="block text-left">
                <span className="mb-1 block text-sm font-bold text-foreground">
                  Message
                </span>
                <textarea
                  required
                  maxLength={5000}
                  rows={5}
                  value={contactForm.message}
                  onChange={(event) =>
                    setContactForm({ ...contactForm, message: event.target.value })
                  }
                  className="w-full resize-y rounded-lg border border-border bg-background px-4 py-3 text-foreground outline-none transition-shadow focus:ring-2 focus:ring-primary"
                />
              </label>

              {contactStatus.type !== 'idle' && (
                <p
                  className={`rounded-lg px-4 py-3 text-sm ${
                    contactStatus.type === 'success'
                      ? 'bg-success/10 text-success'
                      : 'bg-error/10 text-error'
                  }`}
                  role="status"
                >
                  {contactStatus.message}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-lg bg-primary px-6 py-3 font-cta text-base font-bold text-primary-foreground shadow-cta transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default FAQSection;